import { calculateAmortizationSchedule } from "./amortizationCalculator";

/**
 * Calculate the starting balance for restructuring based on the original amortization schedule
 * Finds the next due payment from the inception date and returns its beginning balance
 */
export const calculateStartingBalance = (originalAmortization, inceptionDate, additionalPrincipalAmount = 0) => {
  if (!originalAmortization || originalAmortization.length === 0) {
    return parseFloat(additionalPrincipalAmount || 0);
  }

  const inception = new Date(inceptionDate);

  // Find the next payment due after or on the inception date
  const nextPayment = originalAmortization.find((payment) => {
    const paymentDate = new Date(payment.payment_date);
    return paymentDate >= inception;
  });

  // If we found a next payment, use its beginning balance, otherwise use the last payment's ending balance
  let baseBalance = 0;
  if (nextPayment) {
    baseBalance = nextPayment.beginning_balance || 0;
  } else if (originalAmortization.length > 0) {
    // If no future payments, the loan is paid off or use the last balance
    const lastPayment = originalAmortization[originalAmortization.length - 1];
    baseBalance =
      lastPayment.beginning_balance - lastPayment.principal_payment - (lastPayment.extra_principal_payment || 0);
  }

  const additionalAmount = parseFloat(additionalPrincipalAmount || 0);
  const totalBalance = baseBalance + additionalAmount;
  // Add any additional principal amount (fees, etc.)
  return totalBalance;
};

/**
 * Extract the regular scheduled payment amount from the amortization schedule
 * Finds the first non-interest-only payment to get the regular payment amount
 */
export const calculateScheduledPayment = (amortizationSchedule, interest_only_periods = 0) => {
  if (!amortizationSchedule || amortizationSchedule.length === 0) {
    return null;
  }

  const ioPeriods = parseInt(interest_only_periods || 0, 10);

  // Find the first payment after interest-only periods
  // This will be the regular scheduled payment amount
  const firstAmortizingPayment = amortizationSchedule.find((payment, index) => {
    return index >= ioPeriods && payment.payment_amount > 0;
  });

  if (firstAmortizingPayment) {
    return firstAmortizingPayment.payment_amount;
  }

  // If no amortizing payments found, return the first payment amount
  return amortizationSchedule[0]?.payment_amount || null;
};

export const calculateScenarioAmortization = (debtSchedule, scenario, originalAmortization = []) => {
  const {
    interest_rate,
    maturity_date,
    payment_frequency,
    interest_only_periods,
    inception_date,
    additional_principal_amount,
  } = scenario;
  const { day_count_convention } = debtSchedule;

  if (
    !maturity_date ||
    interest_rate === undefined ||
    interest_rate === null ||
    !payment_frequency ||
    !inception_date
  ) {
    return { schedule: [], scheduledPayment: null };
  }

  try {
    // Calculate the starting balance based on the original amortization schedule
    const startingBalance = calculateStartingBalance(originalAmortization, inception_date, additional_principal_amount);

    const schedule = calculateAmortizationSchedule({
      inception_date: inception_date,
      maturity_date,
      interest_rate,
      payment_frequency,
      amount: startingBalance,
      balloon_amount: scenario.balloon_amount || 0,
      extra_principal_rate: 0, // Restructuring doesn't use extra principal by default
      day_count_convention: day_count_convention || "30/360",
      interest_only_periods: interest_only_periods || 0,
    });

    // Extract the regular scheduled payment from the generated schedule
    const scheduledPayment = calculateScheduledPayment(schedule, interest_only_periods || 0);

    // Add extra fields that the restructuring view expects
    const enrichedSchedule = schedule.map((payment, index) => ({
      ...payment,
      id: index + 1,
      is_interest_only: index + 1 <= parseInt(interest_only_periods || 0, 10),
      days_in_period:
        day_count_convention === "Actual/365"
          ? index > 0
            ? Math.round(
                365 /
                  (payment_frequency === "Weekly"
                    ? 52
                    : payment_frequency === "Bi-weekly"
                      ? 26
                      : payment_frequency === "Quarterly"
                        ? 4
                        : payment_frequency === "Semi-Annual"
                          ? 2
                          : payment_frequency === "Annual"
                            ? 1
                            : 12)
              )
            : 30
          : 30,
    }));

    return {
      schedule: enrichedSchedule,
      scheduledPayment,
    };
  } catch (error) {
    console.error("Error calculating amortization schedule", error);
    return { schedule: [], scheduledPayment: null };
  }
};
